import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useState, type ComponentProps, type ReactNode } from 'react';
import { Button } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';

export interface RefreshButtonProps extends Omit<
  ComponentProps<typeof Button>,
  'children' | 'onClick'
> {
  queryKey: QueryKey;
  label?: ReactNode;
  exact?: boolean;
  onSuccess?: () => void | Promise<void>;
  onError?: (error: unknown) => void;
}

export const RefreshButton = ({
  queryKey,
  label = 'Refresh',
  exact = false,
  onSuccess,
  onError,
  className,
  disabled,
  variant = 'outline',
  size = 'sm',
  type = 'button',
  ...buttonProps
}: RefreshButtonProps) => {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (isRefreshing) return;

    setIsRefreshing(true);

    try {
      await queryClient.invalidateQueries({ queryKey, exact });
      await onSuccess?.();
    } catch (error) {
      if (onError) {
        onError(error);
      } else {
        console.error('Failed to refresh queries', error);
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <Button
      {...buttonProps}
      type={type}
      variant={variant}
      size={size}
      className={cn('gap-2', className)}
      onClick={handleRefresh}
      disabled={disabled || isRefreshing}
      loading={isRefreshing}
      aria-busy={isRefreshing}
    >
      {!isRefreshing ? <RefreshCw className="size-4" /> : null}
      {label}
    </Button>
  );
};

export default RefreshButton;
